// Spec §4.3: Extract useMediaQuery hook logic for responsive icon/button sizing
import { useEffect, useState } from 'react';

/**
 * Tracks whether the viewport satisfies a CSS media query for responsive rendering. Seeds state from an SSR-safe matchMedia check, re-evaluates on mount and whenever the query changes, and subscribes to change events through addListener or addEventListener, removing the listener on cleanup.
 *
 * @param query - CSS media query to track.
 * @returns True while the viewport matches the query.
 */
function useMediaQuery(query: string) {
  /**
   * Checks whether the viewport currently matches a CSS media query, safely outside the browser.
   * Returns window.matchMedia's matches flag when window exists and false otherwise.
   *
   * @param q - CSS media query to evaluate.
   * @returns True when the viewport matches the query.
   */
  const getMatches = (q: string) => {
    if (typeof window !== 'undefined') {
      return window.matchMedia(q).matches;
    }
    return false;
  };

  const [matches, setMatches] = useState(getMatches(query));

  /**
   * Syncs the tracked match state after the media query result may have changed.
   * Re-evaluates the hook's query through getMatches and stores the result.
   *
   * @returns Nothing.
   */
  function handleChange() {
    setMatches(getMatches(query));
  }

  useEffect(() => {
    const matchMedia = window.matchMedia(query);

    handleChange();

    if (matchMedia.addListener) {
      matchMedia.addListener(handleChange);
    } else {
      matchMedia.addEventListener('change', handleChange);
    }

    return () => {
      if (matchMedia.removeListener) {
        matchMedia.removeListener(handleChange);
      } else {
        matchMedia.removeEventListener('change', handleChange);
      }
    };
  }, [query]);

  return matches;
}

/**
 * Reports whether the modal header should switch to its mobile layout. Evaluates a max-width 599px media query through useMediaQuery.
 *
 * @returns Whether the viewport is at mobile width.
 */
export const useModalHeader = () => {
  const isMobile = useMediaQuery('(max-width: 599px)');

  return { isMobile };
};
